// Types + TanStack Query hook for the Earnings Calendar "Week Ahead" board.
//
// Auth / transport: we reuse the project's shared authenticated Axios instance
// (`apiClient` from src/api/client.ts). Its baseURL already includes the
// `/api/v1` version prefix and attaches the WordPress-issued JWT
// (`Authorization: Bearer <token>`) + the `forge_session` cookie on every
// request, and centralises the 401 -> silent-refresh -> WP-login-bounce
// handling. Because `/api/v1` is baked into baseURL, we request the BARE
// `/earnings-calendar/...` path here (adding `/api/v1` would double it).
//
// The backend serves this entirely off the pre-computed
// `earnings_calendar_prominent` hypertable (no live FMP call on the request
// path), so the round-trip is dominated by network latency, not compute.

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../../../api/client';

export type MarketType = 'US' | 'DE';

// Field names mirror the backend Pydantic aliases EXACTLY (camelCase on the
// wire): EarningItem carries epsEstimated/revenueEstimated/reportingTime/
// marketCap aliases, and EarningsCalendarResponse carries startDate/endDate.
export interface EarningItem {
    symbol: string;
    name: string;
    epsEstimated: number | null;
    revenueEstimated: number | null;
    reportingTime: string | null;
    marketCap: number | null;
}

export interface DailyEarnings {
    date: string; // ISO string "YYYY-MM-DD"
    items: EarningItem[];
}

export interface EarningsCalendarResponse {
    market: MarketType;
    startDate: string;
    endDate: string;
    days: DailyEarnings[];
}

const fetchEarnings = async (
    market: MarketType,
): Promise<EarningsCalendarResponse> => {
    const { data } = await apiClient.get<EarningsCalendarResponse>(
        '/earnings-calendar/week-ahead',
        { params: { market } },
    );
    return data;
};

export const useEarningsCalendar = (market: MarketType) => {
    return useQuery({
        queryKey: ['earnings-calendar', market],
        queryFn: () => fetchEarnings(market),
        // The board is refreshed once daily by the ARQ worker, so a 5-minute
        // staleTime is safe and spares needless refetches.
        staleTime: 5 * 60 * 1000,
    });
};
